import { JustRightHeightModal } from 'components/styled/scoped';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { T, useTranslate } from '@tolgee/react';
import {
  Button,
  ModalProps,
  Upload,
  message,
  Progress,
  Table,
  Tooltip,
  Popconfirm,
  Form,
} from 'antd';
import DepartmentItemSelect from 'components/AuthShop/components/DepartmentItemSelect';
import {
  CheckCircleFilled,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleFilled,
  CloseCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  InfoCircleFilled,
  LoadingOutlined,
  QuestionCircleOutlined,
  RightCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { ReactComponent as ExcelSvg } from '../../assets/excel.svg';
import { css } from '@emotion/css';
import chatttSDK from 'services/chattt';
import AuthShopHelper from 'components/AuthShop/AuthShopHelper';
import { match, when } from 'ts-pattern';
import { filter, get, sortBy, findIndex, cloneDeep } from 'lodash-es';
import { useAuthorization } from 'components/Authorization';
import { useRequest, useUnmount } from 'ahooks';
import Checkbox from 'antd/es/checkbox/Checkbox';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { browserDownload } from 'utils/download';
import dayjs from 'dayjs';
import posthog from '@leyan/lytics';
import { notifyAuthShopNeedHelp } from 'services/api/admin';
import CheckboxGroup from 'components/AuthShop/components/CheckboxGroup';

interface LazadaMultiAuthModalProps extends ModalProps {
  goSingle: () => void;
}
export const LazadaMultiAuthModal = memo((props: LazadaMultiAuthModalProps) => {
  const { t } = useTranslate();
  const { onCancel, goSingle, ...restProps } = props;
  const [departmentIds, setDepartmentIds] = useState<number[]>([]);
  const [file, setFile] = useState<any>();
  const { token } = useAuthorization<Oversea.Token.Payload>();
  const { data, runAsync } = useRequest(
    (data) => {
      if (!chatttSDK) return Promise.reject();
      return chatttSDK?.lazada?.startMultiAuth(data);
    },
    {
      manual: true,
    },
  );
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const total = get(file, 'response.rows.length', 0);
  const [progressData, setProgressData] = useState<RPA.Lazada.LazadaMultiAuthProgressData>();
  const handleNeedHelp = useCallback(async () => {
    posthog.capture('$authLazadaShops', {
      result: JSON.stringify({
        platform: 'shopee',
        orgId: payload?.orgId,
        phoneNumber: payload?.phoneNumber,
        entry: 'batch',
      }),
    });
    try {
      const data = await notifyAuthShopNeedHelp();
      if (data?.data?.code === 200) {
        message.success(t('authShop.已经安排客户服务专家联系您，请稍后'));
      } else {
        throw new Error('请求发送失败');
      }
    } catch (err) {
      message.error(t('authShop.请求发送失败'));
    }
  }, [payload, t]);
  const [isOnlyFailed, setIsOnlyFailed] = useState(false);
  const [retryRowNumbers, setRetryRowNumbers] = useState<number[]>([]);
  const systemLanguage = useSystemLanguageValue();
  const downloadUrl =
    systemLanguage === 'zh-CN'
      ? 'https://clients.zhisuitech.com/fett-public/lazada%E6%89%B9%E9%87%8F%E6%8E%88%E6%9D%83%E6%A8%A1%E7%89%88.xlsx'
      : 'https://clients.zhisuitech.com/fett-public/lazada_batch_authorization_template.xlsx';

  const silence = !!get(data, 'id') && !get(progressData, 'done');

  const handleRetry = useCallback(
    (rows: RPA.Lazada.LazadaMultiAuthRow[]) => {
      setRetryRowNumbers((prev) => {
        return [...new Set([...prev, ...rows.map((e) => e.rowNumber)])];
      });
      chatttSDK?.lazada?.retryAuth({
        id: data?.id!,
        rows,
      });
    },
    [data],
  );

  useEffect(() => {
    const handler = (evt: MessageEvent) => {
      const { event, payload } = evt.data;
      const eventName = event as RPA.Lazada.DispatchToWebMessageHooks;
      if (eventName === 'lazadaMultiAuth:progress') {
        if (get(payload, 'data.taskId') === data?.id) {
          setProgressData(payload.data);
        }
      }
      if (eventName === 'lazadaMultiAuth:retryProgress') {
        setRetryRowNumbers((prev) => {
          const copy = cloneDeep(prev);
          const index = findIndex(copy, (rowNumber) => rowNumber === payload);
          if (index > -1) {
            copy.splice(index, 1);
            return copy;
          } else {
            return prev;
          }
        });
      }
    };

    window.addEventListener('message', handler, false);
    return () => {
      window.removeEventListener('message', handler);
    };
  }, [data]);

  const allRows = useMemo(() => {
    return sortBy(
      [...get(progressData, 'successData', []), ...get(progressData, 'errorData', [])],
      'rowNumber',
    );
  }, [progressData]);

  const renderTableData = useMemo(() => {
    if (isOnlyFailed) {
      return filter(allRows, (row) => !!row.failCode);
    }
    return allRows;
  }, [isOnlyFailed, allRows]);
  const handleCancel = () => {
    if (data?.id) {
      chatttSDK?.lazada?.closeAuth(data.id);
    }
    onCancel?.(data?.id as any);
  };
  useUnmount(() => {
    if (data?.id) {
      chatttSDK?.lazada?.closeAuth(data.id);
    }
  });
  const [shopType, setShopType] = useState<string>('LOCAL');

  const failCodeRender = (value: string) => {
    return match(value)
      .with('GET_AUTH_URL_FAIL', () => {
        return t('authShop.获取Lazada授权链接失败');
      })
      .with('CANCEL', () => {
        return t('ViewShops.manualCancel');
      })
      .with('RETRYTOOMUCH', () => {
        return t('重试过多');
      })
      .with('GET_RESULT_FAIL', () => {
        return t('marketing.获取授权信息失败，请稍后再试');
      })
      .with('GET_RESULT_FAIL', () => {
        return t('marketing.获取授权信息失败，请稍后再试');
      })
      .with('TIME_OUT', () => {
        return t('超时，可能网络有延迟');
      })
      .with('SCRIPT_FAILED', () => {
        return t('执行失败，可能邮箱或密码错误');
      })
      .otherwise(() => null);
  };

  const step1 = (
    <>
      <DepartmentItemSelect
        platform="lazada"
        value={departmentIds}
        onChange={setDepartmentIds}
        showRequiredMark={false}
      />

      <div className="flex items-center justify-between">
        <div className="leading-[32px] font-bold">{t('ViewShops.上传文件')}</div>
        <div>
          <a download href={downloadUrl}>
            <DownloadOutlined />
            {t('aide.下载模版')}
          </a>
        </div>
      </div>

      <Upload.Dragger
        name="file"
        accept=".xlsx,.xls"
        maxCount={1}
        onChange={(file) => {
          if (file.file.status !== 'error') {
            setFile(file.file);
          }
        }}
        showUploadList={false}
        customRequest={(options) => {
          const { file, onError, onSuccess } = options;
          if (file instanceof Blob) {
            const reader = new FileReader();
            reader.onload = (e) => {
              const data = e.target!.result as unknown as ArrayBuffer;
              chatttSDK &&
                chatttSDK.lazada
                  .execAuthExcel(data)
                  .then((res) => {
                    if (res.status === 'success') {
                      onSuccess?.(res);
                    } else {
                      message.error(
                        res.msg === 'format_error' ? t('格式错误，邮箱请去除超链接') : res.msg,
                      );
                      onError?.(new Error(res.msg));
                    }
                  })
                  .catch(() => {
                    message.error(t('ViewsChat.上传失败'));
                    onError?.(new Error('Upload failed'));
                  });
            };
            reader.readAsArrayBuffer(file);
          }
        }}
        className={css`
          &:hover {
            background-color: var(--ant-primary-1);
          }
          display: ${!file ? 'block' : 'none'};
        `}
      >
        <ExcelSvg className="w-[25px] h-[25px]" />
        <div className="text-[var(--ant-primary-color)]">
          {t('ViewShops.shopee.multi.auth.xls.upload.text')}
        </div>
        <div className="text-[#b5b9bf]">{t('ViewShops.shopee.multi.auth.xls.upload.hint')}</div>
      </Upload.Dragger>
      {match(file)
        .with({ status: 'uploading' }, () => {
          return (
            <div className="bg-[#fafafa] text-center rounded-[2px] w-full h-full py-[16px] border border-dashed border-[#d9d9d9]">
              <div className="flex items-center px-[28px] overflow-hidden">
                <LoadingOutlined style={{ fontSize: 32, flexShrink: 0 }} />
                <div className="ml-[10px] line-clamp-2">{file?.name}</div>
              </div>
            </div>
          );
        })
        .with({ status: when((t) => ['done', 'success'].includes(t)) }, () => {
          return (
            <div className="flex items-center p-[16px] bg-[#fafafa] rounded-[2px] border border-dashed border-[#d9d9d9]">
              <ExcelSvg className="w-[25px] h-[25px] flex-shrink-0" />
              <div className="flex-grow overflow-hidden ml-[6px]">
                <div className="truncate mb-[4px]">{file?.name}</div>
                <div>
                  <T
                    keyName="ViewShops.shopee.multi.interrupt.modal.auth.accounts"
                    params={{
                      count: get(file, 'response.rows.length', 0),
                      strong: <strong />,
                    }}
                  />
                </div>
              </div>
              <DeleteOutlined
                className="flex-shrink-0"
                onClick={() => {
                  setFile(undefined);
                }}
              />
            </div>
          );
        })
        .otherwise(() => {
          return null;
        })}
    </>
  );

  const step2 = progressData?.done ? (
    <>
      {get(progressData, 'errorData.length', 0) > 0 && (
        <>
          <div>
            <div className="flex  px-[16px] py-[8px] bg-[#f0f4ff] items-center">
              <ExclamationCircleOutlined
                style={{ marginRight: 8, color: `var(--ant-warning-color)` }}
              />
              <div>{t('ViewShops.shopee.auth.progress.only.fail.result.fail.tips')}</div>
            </div>
          </div>
          <div className="flex mb-[12px] border-0 border-b border-solid border-[#f0f2f5] justify-between py-[12px]">
            <div className="flex items-center">
              <Checkbox checked={isOnlyFailed} onChange={(e) => setIsOnlyFailed(e.target.checked)}>
                {t('ViewShops.shopee.auth.progress.only.fail.result')}
              </Checkbox>
              <Button
                icon={<SyncOutlined />}
                type="primary"
                onClick={() => {
                  handleRetry(filter(allRows, (row) => !!row.failCode));
                }}
              >
                {t('ViewShops.shopee.multi.interrupt.modal.result.fail.retry')}
              </Button>
            </div>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => {
                chatttSDK?.lazada
                  ?.downloadFailExcel({
                    templateUrl: downloadUrl,
                    id: data?.id!,
                  })
                  .then((res) => {
                    if (res.status === 'success') {
                      const blob = new Blob([res.data], { type: 'application/octet-stream' });
                      const url = URL.createObjectURL(blob);
                      browserDownload(
                        url,
                        `${
                          systemLanguage === 'zh-CN'
                            ? 'lazada批量授权失败'
                            : 'lazada_authorization_fail'
                        }_${dayjs().format('YYYY-MM-DD_HH:mm:ss')}.xlsx`,
                      );
                      URL.revokeObjectURL(url);
                    }
                  });
              }}
            >
              {t('ViewShops.shopee.multi.interrupt.modal.result.fail.export')}
            </Button>
          </div>
        </>
      )}
      <div className="flex items-center py-[8px] gap-[20px]">
        <div className="flex gap-[8px] items-center">
          <RightCircleOutlined style={{ color: 'var(--ant-primary-color)' }} />
          {t('ViewShops.shopee.auth.progress.account.total.title', {
            x:
              get(progressData, 'successData.length', 0) + get(progressData, 'errorData.length', 0),
          })}
        </div>
        <div className="flex gap-[8px] items-center">
          <CheckCircleOutlined style={{ color: 'var(--ant-success-color)' }} />
          {t('ViewShops.shopee.auth.progress.account.success.title', {
            x: get(progressData, 'successData.length', 0),
          })}
        </div>
        <div className="flex gap-[8px] items-center">
          <CloseCircleOutlined style={{ color: 'var(--ant-error-color)' }} />
          {t('ViewShops.shopee.auth.progress.account.fail.title', {
            x: get(progressData, 'errorData.length', 0),
          })}
        </div>
      </div>
      <Table<RPA.Lazada.LazadaMultiAuthRow>
        rowKey="rowNumber"
        dataSource={renderTableData}
        scroll={{ y: 300 }}
        columns={[
          {
            key: 'rowNumber',
            dataIndex: 'rowNumber',
            title: t('行号'),
            width: 80,
          },
          {
            title: t('ViewShops.shopee.multi.interrupt.modal.result.col.region'),
            dataIndex: 'regionName',
            key: 'regionName',
          },
          {
            key: 'account',
            dataIndex: 'account',
            title: t('field.email'),
          },
          {
            key: 'status',
            title: t('ViewShops.shopee.multi.interrupt.modal.result.col.result'),
            render: (_, record) => {
              if (!!record.failCode) {
                return (
                  <div className="flex items-center">
                    <CloseCircleFilled className="mr-[5px] !text-[var(--ant-error-color)]" />
                    {t('ViewShops.shopee.multi.interrupt.modal.result.status.failed')}
                  </div>
                );
              } else {
                return (
                  <>
                    <CheckCircleFilled className="mr-[5px] !text-[var(--ant-success-color)]" />
                    {t('ViewShops.shopee.multi.interrupt.modal.result.status.success')}
                  </>
                );
              }
            },
          },
          {
            title: t('ViewShops.shopee.multi.interrupt.modal.result.col.desc'),
            dataIndex: 'failCode',
            key: 'failCode',
            render: (value) => {
              return failCodeRender(value);
            },
          },
          {
            title: t('操作'),
            key: 'operation',
            render: (_, record) => {
              if (!!record.failCode) {
                return (
                  <Button
                    loading={retryRowNumbers.includes(record.rowNumber)}
                    onClick={() => {
                      handleRetry([record]);
                    }}
                    type="link"
                  >
                    {t('ViewShops.shopee.auth.result.reauthorize')}
                  </Button>
                );
              } else {
                return '-';
              }
            },
          },
        ]}
        pagination={{
          total: renderTableData.length,
        }}
      />
    </>
  ) : (
    <>
      {t('本次共授权 Lazada 平台 x 个帐号', { x: total })}
      <Progress
        percent={
          Math.floor(
            ((get(progressData, 'successData.length', 0) +
              get(progressData, 'errorData.length', 0)) /
              get(file, 'response.rows.length', 1)) *
              100,
          ) || 0
        }
      />
      <div>
        <ClockCircleOutlined style={{ color: 'var(--ant-warning-color)' }} />
        <span>
          {' '}
          {t('ViewShops.shopee.auth.progress.account.wait.title', {
            x:
              total -
              get(progressData, 'successData.length', 0) -
              get(progressData, 'errorData.length', 0) -
              get(progressData, 'pending', 0),
          })}
        </span>
      </div>
      <div>
        <CheckCircleOutlined style={{ color: 'var(--ant-success-color)' }} />
        <span>
          {' '}
          {t('ViewShops.shopee.auth.progress.account.success.title', {
            x: get(progressData, 'successData.length', 0),
          })}
        </span>
      </div>
      <div>
        <CloseCircleOutlined style={{ color: 'var(--ant-error-color)' }} />
        <span>
          {' '}
          {t('ViewShops.shopee.auth.progress.account.fail.title', {
            x: get(progressData, 'errorData.length', 0),
          })}
        </span>
      </div>
      <Table
        size="small"
        style={{ marginTop: 10 }}
        scroll={{ y: 210 }}
        columns={[
          {
            title: t('aide.站点'),
            dataIndex: 'regionName',
          },
          {
            title: t('field.email'),
            dataIndex: 'account',
          },
          {
            title: t('ViewShops.shopee.multi.interrupt.modal.result.col.desc'),
            dataIndex: 'failCode',
            render: (value) => {
              return failCodeRender(value);
            },
          },
        ]}
        dataSource={get(progressData, 'errorData', [])}
        pagination={false}
      />

      <Popconfirm
        title={t('ViewShops.shopee.auth.progress.terminal.title')}
        onConfirm={() => {
          chatttSDK?.lazada?.terminateAuth(data?.id!);
        }}
      >
        <div className="inline-block mt-[30px] text-[#88898c] cursor-pointer [&_.anticon]:ml-[5px]">
          {t('ViewShops.shopee.auth.progress.terminal.btn.text')}
          <Tooltip title={t('ViewShops.shopee.auth.progress.terminal.tips')}>
            <QuestionCircleOutlined />
          </Tooltip>
        </div>
      </Popconfirm>
    </>
  );

  return (
    <div
      className={
        data?.id && !progressData?.done
          ? css`
              .ant-modal-mask,
              .ant-modal-wrap {
                top: 80px;
                left: 265px;
              }
            `
          : ''
      }
    >
      <JustRightHeightModal
        title={silence ? null : t('authShop.添加Lazada店铺')}
        keyboard={!silence}
        closable={!silence}
        width={progressData?.done ? 1060 : 660}
        maskClosable={false}
        mask
        centered
        getContainer={false}
        closeIcon={
          !!file && !data?.id ? (
            <>
              <Popconfirm
                onConfirm={handleCancel}
                title={t('ViewShops.shopee.multi.auth.xls.uploaded.confirm.tips')}
              >
                <CloseOutlined />
              </Popconfirm>
            </>
          ) : (
            <CloseOutlined onClick={handleCancel} />
          )
        }
        className={css`
          .ant-modal-body {
            padding: 16px !important;
            margin: 0 !important;
          }
        `}
        footer={
          data?.id ? null : (
            <div className="flex items-center justify-between">
              <AuthShopHelper display="tips" onNeedHelp={handleNeedHelp} noReason3 />
              <div>
                {!!file && !data?.id ? (
                  <Popconfirm
                    title={t('ViewShops.shopee.multi.auth.xls.uploaded.confirm.tips')}
                    onConfirm={handleCancel}
                  >
                    <Button>{t('phrase.取消')}</Button>
                  </Popconfirm>
                ) : (
                  <Button onClick={handleCancel}>{t('phrase.取消')}</Button>
                )}
                <Button
                  type="primary"
                  disabled={!['success', 'done'].includes(file?.status) || shopType === 'LOCAL'}
                  onClick={() => {
                    runAsync({
                      rows: file?.response?.rows || [],
                      token: token,
                      departmentIds,
                    });
                  }}
                >
                  {t('ViewShops.modal.ok.go.auth')}
                </Button>
              </div>
            </div>
          )
        }
        {...restProps}
      >
        {match(data)
          .with({ id: when((t) => !!t) }, () => {
            return step2;
          })
          .otherwise(() => {
            return (
              <>
                <Form.Item
                  className={css`
                    .ant-form-item-label {
                      font-weight: 500;
                      width: 100%;
                      text-align: left;
                    }
                  `}
                  label={t('authShop.请选择您要添加的店铺类型')}
                >
                  <CheckboxGroup
                    className={css`
                      .anticon {
                        font-size: 20px !important;
                      }
                      .ant-col {
                        flex-basis: auto !important;
                        margin-bottom: 0 !important;
                        & > div {
                          padding-left: 32px;
                          padding-right: 32px;
                          width: fit-content;
                        }
                      }
                    `}
                    options={[
                      {
                        value: 'CROSS',
                        render: <>{t('ViewShops.CROSS')}</>,
                      },
                      {
                        value: 'LOCAL',
                        render: <>{t('ViewShops.LOCAL')}</>,
                      },
                    ]}
                    value={shopType}
                    onChange={(shopType: string) => {
                      setShopType(shopType);
                    }}
                  />
                </Form.Item>
                {shopType === 'CROSS' ? (
                  step1
                ) : (
                  <div className="flex bg-[#fff7f7] px-[16px] py-[12px] gap-[4px]">
                    <InfoCircleFilled style={{ color: '#f05b5b', marginTop: 4 }} />
                    <div>
                      <span>
                        {t(
                          '根据Lazada官方规定，所有本土店铺需先登录服务市场并完成第三方应用(ChatPlusAI)的订购后才能进行授权，无法进行批量操作，请通过单店添加完成授权。',
                        )}
                      </span>
                      <Button
                        type="link"
                        className="!block !p-0 !h-[24px]"
                        onClick={() => {
                          goSingle?.();
                          onCancel?.(null!);
                        }}
                      >
                        {t('去添加店铺')}&gt;
                      </Button>
                    </div>
                  </div>
                )}
              </>
            );
          })}
      </JustRightHeightModal>
    </div>
  );
});

LazadaMultiAuthModal.displayName = 'LazadaMultiAuthModal';
